import { ProviderType } from '../generated/graphql'
import { providerProperties } from '../provider/provider-properties'
import { reduceToSumOfItems, sumItems } from '../util/array-utility'
import type { ResultFilter } from './calculator-result-utility'
import {
  checkForInvalid,
  createFromEnvironment,
  EnergyType,
  PrimaryEnergyFlow,
} from './EnergyFlow'
import { CirculationHeatPumpProvider } from './providers/CirculationHeatPumpProvider'
import { Provider } from './providers/common/Provider'
import {
  HybridHeatPumpProvider,
  isHybridHeatPumpProvider,
} from './providers/HybridHeatPumpProvider'
import { SpecialDemand } from './special-demand-ids'

const transferredDemandIds: string[] = [
  SpecialDemand.Buildings,
  SpecialDemand.LocalWaterGrid,
]
const filterOutTransferredDemands = (demand: PrimaryEnergyFlow) =>
  !transferredDemandIds.includes(demand.demandId)

export const getValuesFromDemands = (
  demands: PrimaryEnergyFlow[],
  filter: ResultFilter,
): number[] => {
  const partiallyFilteredDemands = filter.includeTransferredDemands
    ? demands
    : demands.filter(filterOutTransferredDemands)

  return partiallyFilteredDemands
    .filter((demand) => demand.energyType === filter.energyType)
    .map((flow) => flow.hourSeries)
    .reduce(reduceToSumOfItems, [])
}

const createFilterProviderByPropertyIfExists =
  (filter: ResultFilter) => (provider: Provider) => {
    if (!filter.providerProperty) {
      return true
    }

    // Hybrid heat pumps are environmental heat based only when using air or ground to produce heat
    if (
      filter.providerProperty === 'isEnvironmentalHeat' &&
      provider.type === ProviderType.HybridHeatPump
    ) {
      return (provider as HybridHeatPumpProvider).isEnvironmentalHeatUsing
    }

    // Circulation and hybrid heat pumps are waste energy based only when connected to waste heat
    if (
      filter.providerProperty === 'isWasteEnergyBased' &&
      [ProviderType.CirculationHeatPump, ProviderType.HybridHeatPump].includes(
        provider.type,
      )
    ) {
      return (provider as CirculationHeatPumpProvider | HybridHeatPumpProvider)
        .isConnectedToWasteHeat
    }

    return providerProperties[provider.type][filter.providerProperty]
  }

const patchHybridHeatPumpResults = (
  provider: HybridHeatPumpProvider,
  filter: ResultFilter,
) => {
  const flows = provider.flows ?? []

  if (filter.providerProperty === 'isEnvironmentalHeat') {
    // Instead of directly returning FromEnvironment/Heating, return ground + air source usage.
    return createFromEnvironment(
      EnergyType.Heating,
      EnergyType.Heating,
      sumItems([
        provider.extraOutputValues.groundSourceHeating,
        provider.extraOutputValues.airSourceHeating,
      ]),
    )
  }

  if (filter.providerProperty === 'isWasteEnergyBased') {
    // Instead of directly returning FromEnvironment/Heating, return waste source usage.
    return createFromEnvironment(
      EnergyType.Heating,
      EnergyType.Heating,
      provider.extraOutputValues.wasteSourceHeating,
    )
  }

  return flows
}

export const getValuesFromProviders = (
  providers: Provider[],
  filter: ResultFilter,
): number[] => {
  const { hybridHeatPumpExtraOutputKey, additionalFlowFilter } = filter
  if (hybridHeatPumpExtraOutputKey) {
    return providers
      .filter(isHybridHeatPumpProvider)
      .map((provider) => {
        if (!provider.extraOutputValues) {
          throw new Error(
            'Trying to get values from hybrid heat pump but calculation has not been done yet',
          )
        }

        return provider.extraOutputValues[hybridHeatPumpExtraOutputKey]
      })
      .reduce(reduceToSumOfItems, [])
  }
  return providers
    .filter(createFilterProviderByPropertyIfExists(filter))
    .flatMap((provider) =>
      isHybridHeatPumpProvider(provider)
        ? patchHybridHeatPumpResults(provider, filter)
        : provider.flows,
    )
    .filter(checkForInvalid)
    .filter((flow) =>
      filter.quadrant ? flow.quadrant === filter.quadrant : true,
    )
    .filter((flow) =>
      filter.energyType ? flow.energyType === filter.energyType : true,
    )
    .filter((flow) =>
      filter.wasteType ? flow.wasteType === filter.wasteType : true,
    )
    .filter(additionalFlowFilter ?? Boolean)
    .map((flow) => flow.hourSeries)
    .reduce(reduceToSumOfItems, [])
}
